import React, { useState, useEffect } from 'react';
import { wardInventoryAPI, pharmacyAPI } from '../../utils/api';
import { toast } from 'react-toastify';

const inp = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lbl = { fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4, color: '#374151' };

const STATUS_STYLE = {
  Pending:  { background: '#fef3c7', color: '#92400e' },
  Issued:   { background: '#d1fae5', color: '#065f46' },
  Rejected: { background: '#fee2e2', color: '#dc2626' },
  Partial:  { background: '#dbeafe', color: '#1e40af' },
};

const GENERAL_ITEMS = [
  // Stationery
  'A4 Paper (Ream)', 'Register / Log Book', 'Ball Point Pen', 'Stapler + Pins', 'Scotch Tape', 'File Folder', 'Clipboard', 'Marker (Permanent)', 'Rubber Band', 'Printed Forms',
  // Housekeeping
  'Disinfectant (Dettol)', 'Floor Cleaner', 'Toilet Cleaner', 'Mop + Bucket', 'Broom', 'Dustbin Liner Bags', 'Tissue Paper Roll', 'Hand Wash Soap', 'Air Freshener', 'Cleaning Cloth',
  // Linen
  'Bed Sheet', 'Pillow Cover', 'Patient Gown', 'Blanket', 'Towel',
  // General Supplies
  'Gloves (Non-Sterile)', 'Face Mask', 'Hand Sanitizer', 'Apron', 'Shoe Cover',
];

const emptyItem = () => ({ item_name: '', qty_requested: '', unit: '', custom: false });

function ItemRow({ item, i, drugs, itemType, onUpdate, onRemove, showRemove }) {
  const isCustom = item.custom || item.item_name === '__custom__';

  const handleSelect = (val) => {
    const drug = drugs.find(d => d.name === val);
    onUpdate(i, 'item_name', val);
    if (drug?.form) onUpdate(i, 'unit', drug.form);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '3fr 100px 130px 30px', gap: 8, marginBottom: 7, alignItems: 'center' }}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        {/* Toggle buttons — always visible */}
        <button type="button" onClick={() => { onUpdate(i, 'custom', true);  onUpdate(i, 'item_name', ''); }} title="Type manually" style={{
          width: 34, height: 34, border: '1px solid', borderRadius: 5, cursor: 'pointer',
          fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: isCustom ? '#1e3a5f' : '#f1f5f9',
          color: isCustom ? '#fff' : '#64748b',
          borderColor: isCustom ? '#1e3a5f' : '#e2e8f0',
          flexShrink: 0,
        }}>✏️</button>
        <button type="button" onClick={() => { onUpdate(i, 'custom', false); onUpdate(i, 'item_name', ''); }} title="Select from list" style={{
          padding: '0 8px', height: 34, border: '1px solid', borderRadius: 5, cursor: 'pointer',
          fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3,
          background: !isCustom ? '#1e3a5f' : '#f1f5f9',
          color: !isCustom ? '#fff' : '#64748b',
          borderColor: !isCustom ? '#1e3a5f' : '#e2e8f0',
          flexShrink: 0, whiteSpace: 'nowrap',
        }}>List ↩</button>

        {/* Input: dropdown or text based on mode */}
        {!isCustom ? (
          <select value={item.item_name} onChange={e => handleSelect(e.target.value)}
            required={i === 0} style={{ ...inp, flex: 1, background: '#fff' }}>
            <option value="">— Select Item —</option>
            {itemType === 'pharmacy'
              ? drugs.map(d => (
                  <option key={d.id} value={d.name}>
                    {d.name}{d.strength ? ` ${d.strength}` : ''}{d.form ? ` (${d.form})` : ''}
                  </option>
                ))
              : GENERAL_ITEMS.map(g => <option key={g} value={g}>{g}</option>)
            }
          </select>
        ) : (
          <input value={item.item_name} onChange={e => onUpdate(i, 'item_name', e.target.value)}
            placeholder="Type item name..." required={i === 0}
            style={{ ...inp, flex: 1 }} />
        )}
      </div>
      <input type="number" min="1" value={item.qty_requested} onChange={e => onUpdate(i, 'qty_requested', e.target.value)}
        placeholder="Qty" required={i === 0}
        style={{ ...inp, textAlign: 'center' }} />
      <input value={item.unit} onChange={e => onUpdate(i, 'unit', e.target.value)}
        placeholder="pcs / box / vial" style={inp} />
      {showRemove
        ? <button type="button" onClick={() => onRemove(i)}
            style={{ width: 28, height: 28, background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 15, lineHeight: 1 }}>×</button>
        : <div />}
    </div>
  );
}

export default function WardInventoryRequest() {
  const [wards, setWards]         = useState([]);
  const [drugs, setDrugs]         = useState([]);
  const [ward, setWard]           = useState({ id: '', name: '' });
  const [itemType, setItemType]   = useState('pharmacy');
  const [notes, setNotes]         = useState('');
  const [items, setItems]         = useState([emptyItem()]);
  const [submitting, setSubmitting] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [tab, setTab]             = useState('new');

  useEffect(() => {
    wardInventoryAPI.getWards().then(r => setWards(r.data || [])).catch(() => {});
    pharmacyAPI.getDrugs({ active: true }).then(r => setDrugs(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'history')
      wardInventoryAPI.getMyRequests({}).then(r => setMyRequests(r.data || [])).catch(() => {});
  }, [tab]);

  // Reset items when item type changes
  useEffect(() => { setItems([emptyItem()]); }, [itemType]);

  const handleWardChange = (e) => {
    const w = wards.find(w => w.id === parseInt(e.target.value));
    setWard(w ? { id: w.id, name: w.name } : { id: '', name: '' });
  };

  const addItem    = () => setItems(p => [...p, emptyItem()]);
  const removeItem = (i) => setItems(p => p.filter((_, idx) => idx !== i));
  const updateItem = (i, f, v) => setItems(p => p.map((it, idx) => idx === i ? { ...it, [f]: v } : it));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ward.id) return toast.error('Please select a ward');
    const valid = items.filter(it => it.item_name.trim() && it.qty_requested);
    if (!valid.length) return toast.error('Enter at least one item with quantity');
    setSubmitting(true);
    try {
      await Promise.all(valid.map(it =>
        wardInventoryAPI.createRequest({
          ward_id: ward.id, ward_name: ward.name, item_type: itemType,
          item_name: it.item_name.trim(), qty_requested: parseInt(it.qty_requested),
          unit: it.unit || null, notes: notes || null,
        })
      ));
      toast.success(`${valid.length} request(s) submitted successfully`);
      setItems([emptyItem()]);
      setNotes('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div style={{ maxWidth: 860 }}>

      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <span style={{ fontSize: 28 }}>📋</span>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e3a5f', margin: 0 }}>Ward Inventory Request</h2>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Request pharmacy items or general supplies for your ward / department</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[['new', '📝 New Request'], ['history', '📂 My Requests']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
            background: tab === key ? '#1e3a5f' : '#f1f5f9',
            color: tab === key ? '#fff' : '#374151',
          }}>{label}</button>
        ))}
      </div>

      {/* New Request Form */}
      {tab === 'new' && (
        <form onSubmit={handleSubmit}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 20, marginBottom: 16 }}>

            {/* Ward + Item Type */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Ward / Department *</label>
                <select value={ward.id} onChange={handleWardChange} required style={{ ...inp, background: '#fff' }}>
                  <option value="">— Select Ward —</option>
                  {wards.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Item Type *</label>
                <select value={itemType} onChange={e => setItemType(e.target.value)} style={{ ...inp, background: '#fff' }}>
                  <option value="pharmacy">Pharmacy Items (medicines / consumables)</option>
                  <option value="general">General Items (stationery / housekeeping)</option>
                </select>
              </div>
            </div>

            {/* Info Banner */}
            <div style={{
              background: itemType === 'pharmacy' ? '#eff6ff' : '#f0fdf4',
              border: `1px solid ${itemType === 'pharmacy' ? '#bfdbfe' : '#bbf7d0'}`,
              borderRadius: 6, padding: '8px 12px', marginBottom: 14,
              fontSize: 12, color: itemType === 'pharmacy' ? '#1d4ed8' : '#15803d', fontWeight: 500,
            }}>
              {itemType === 'pharmacy'
                ? '💊 Select from pharmacy stock list. Use "Other — type manually" for unlisted items.'
                : '📦 Select from stationery / housekeeping list. Use "Other — type manually" for unlisted items.'}
            </div>

            {/* Items */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={lbl}>Items *</label>
                <button type="button" onClick={addItem}
                  style={{ padding: '5px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#1d4ed8' }}>
                  + Add Item
                </button>
              </div>

              {/* Column labels */}
              <div style={{ display: 'grid', gridTemplateColumns: '3fr 100px 130px 30px', gap: 8, marginBottom: 4 }}>
                {['Item Name', 'Quantity', 'Unit (pcs/box/vial)', ''].map((h, i) => (
                  <span key={i} style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</span>
                ))}
              </div>

              {items.map((item, i) => (
                <ItemRow
                  key={i} item={item} i={i}
                  drugs={drugs} itemType={itemType}
                  onUpdate={updateItem}
                  onRemove={removeItem}
                  showRemove={items.length > 1}
                />
              ))}
            </div>

            {/* Notes */}
            <div>
              <label style={lbl}>Notes (optional)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                placeholder="Additional notes, urgency, or special instructions..."
                style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
          </div>

          <button type="submit" disabled={submitting}
            style={{ padding: '9px 28px', background: submitting ? '#9ca3af' : '#0f766e', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer' }}>
            {submitting ? 'Submitting...' : '✓ Submit Request'}
          </button>
        </form>
      )}

      {/* My Requests History */}
      {tab === 'history' && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Date', 'Ward', 'Type', 'Item Name', 'Qty Req.', 'Qty Issued', 'Unit', 'Status'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {myRequests.length === 0
                ? <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No requests found</td></tr>
                : myRequests.map((r, i) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{new Date(r.created_at).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 600, color: '#1e3a5f' }}>{r.ward_name || '—'}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{ background: r.item_type === 'pharmacy' ? '#eff6ff' : '#f0fdf4', color: r.item_type === 'pharmacy' ? '#1d4ed8' : '#15803d', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                        {r.item_type === 'pharmacy' ? 'Pharmacy' : 'General'}
                      </span>
                    </td>
                    <td style={{ padding: '9px 12px', fontSize: 13 }}>{r.item_name}</td>
                    <td style={{ padding: '9px 12px', fontSize: 13, textAlign: 'center', fontWeight: 600 }}>{r.qty_requested}</td>
                    <td style={{ padding: '9px 12px', fontSize: 13, textAlign: 'center' }}>{r.qty_issued || 0}</td>
                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{r.unit || '—'}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{ ...STATUS_STYLE[r.status] || {}, padding: '3px 9px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>{r.status}</span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
